import {
  ComparatorContext,
  ComponentDescriptor,
  ComponentRenderer,
  LegacyComponentDefinition,
  LegacyOrderDefinition,
  useCurrentStepIndex,
  useField,
  useFields,
  useFieldsAttribute,
  useForm,
  useFormAttribute,
  useWizardService,
} from '@kizi/comparator-core';
import {
  BaseHeadingProps,
  Div,
  DivProps,
  Heading,
  P,
} from '@kizi/core-components';
import { Row, RowProps } from '@kizi/shared-components';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { getAdditionalDefinition } from '../../utils/hardcodedDataTransformers';
import { usePeregrineEventHandler } from '../../utils/helpers';
import {
  CarPremiumContext,
  Field,
  SubmitCurrentFormButton,
  Window,
} from '../index';
import NewPremium from '../NewPremium';
import { SubmitCarApplyButton } from '../wrappers/Button/SubmitCarApplyButton';

interface ApplyPageLayoutProps extends DivProps {
  legacyDefinition: LegacyOrderDefinition[];
  componentDescriptors: ComponentDescriptor[];
}

const InputRow = (props: RowProps) => {
  return <Row gridColumnGap={6} {...props} />;
};

/**
 * TODO: figure out a rule to place the Separator, they don't necessarily go on top of a heading (most of the time they don't)
 * nb: might need to implement a notion of sections

const Separator = (divProps: DivProps) => {
  return (
    <Div
      display="flex"
      px={15}
      mt={6}
      borderBottom="1px solid"
      flex={1}
      color="divider"
      {...divProps}
    />
  );
};
 */

export const getRadioAdditionalProps = (fieldData: any): Record<string, any> =>
  fieldData.rowClass === 'finalQuestionLabel'
    ? {
        variant: 'main',
        _labelProps: { variant: 'finalLabel' },
      }
    : {
        variant: 'main',
      };

export const getCheckboxAdditionalProps = (
  fieldData: any,
): Record<string, any> => ({
  _label: undefined,
  label: fieldData.label,
  labelPosition: 'right',
  variant: 'main',
  css: {
    marginRight: '10px',
  },
});

export const getSelectAdditionalProps = (): Record<string, any> => ({
  _labelProps: { variant: 'finalLabel' },
});

export const getAdditionalProps = (fieldData: any): Record<string, any> => {
  switch (fieldData.type) {
    case 'radio':
      return getRadioAdditionalProps(fieldData);
    case 'single_checkbox':
      return getCheckboxAdditionalProps(fieldData);
    case 'select':
      return getSelectAdditionalProps();
    default:
      return {};
  }
};

export const WizardHeading = ({
  props,
  value,
  conditional,
  name,
  ...definitionProps
}: LegacyComponentDefinition) => {
  const dependency = useField(
    conditional && conditional.field ? '*.' + conditional.field : '',
  );
  const field = useField(name);
  const isVisible = useMemo(() => {
    if (
      dependency &&
      conditional &&
      conditional.hasOwnProperty('match') &&
      conditional.hasOwnProperty('show')
    ) {
      if (dependency.value === conditional.match) return conditional.show;
      return !conditional.show;
    }
    return field?.active !== undefined ? field.active : true;
  }, [dependency, conditional, field?.active]);
  return useMemo(
    () =>
      isVisible ? (
        <Heading
          mb={0}
          as="h4"
          variant={props?.variant || 'H4'}
          children={value}
          {...(definitionProps as BaseHeadingProps)}
        />
      ) : null,
    [isVisible, value],
  );
};

export const setCookie = (name, value, days = 1) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${JSON.stringify(value)};expires=${expires.toUTCString()};path=/`;
};

// LocalStorage utilities for carPremium data
const setCarPremiumData = (data) => {
  try {
    localStorage.setItem('carPremium', JSON.stringify(data));
  } catch (error) {
    console.error('Error storing carPremium in localStorage:', error);
  }
};

export const ApplyPageLayout: React.FC<ApplyPageLayoutProps> = ({
  legacyDefinition,
  componentDescriptors,
  ...props
}) => {
  const wizard = useWizardService();
  const [isLoading, isCbMember] = useFields([
    '*.isWaitingForCbApiResponse',
    '*.is_cb_member',
  ]);
  const currentStepIndex = useCurrentStepIndex() ?? '';
  const formName = useFormAttribute('name', currentStepIndex);
  const fieldNames = useFieldsAttribute('name', currentStepIndex);
  const metaAttributes = useFieldsAttribute('meta', currentStepIndex);

  const context = wizard?.getSnapshot().context;

  const currentStepDefinition = legacyDefinition?.[0]?.steps?.[
    currentStepIndex
  ] ?? { components: [] };
  const groupedLegacyDefinitions = useMemo(() => {
    const { components, __typename = '' } = currentStepDefinition;
    let currentIndex = -1;
    let previousRow = 0;
    const groupedRows: Array<Array<any>> = [];

    const adjustedComponents = getAdditionalDefinition(components, __typename);
    (adjustedComponents ?? []).forEach(
      (legacyComponentDefinition: LegacyComponentDefinition) => {
        if (
          !legacyComponentDefinition.row ||
          legacyComponentDefinition.row === 'prequestions' ||
          legacyComponentDefinition.row !== previousRow
        ) {
          currentIndex += 1;
          if (
            typeof legacyComponentDefinition.row === 'number' &&
            legacyComponentDefinition.row !== previousRow
          ) {
            previousRow = Number(legacyComponentDefinition.row);
          } else {
            previousRow = 0;
          }
        }
        if (!groupedRows[currentIndex]) {
          groupedRows.push([]);
        }
        groupedRows[currentIndex].push(legacyComponentDefinition);
      },
    );
    return groupedRows;
  }, [currentStepDefinition.components]);

  const constructedComponents = useMemo(
    () =>
      groupedLegacyDefinitions
        .map((singleGroup, i) => {
          if (
            singleGroup.length === 1 &&
            singleGroup[0].type === 'heading' &&
            !singleGroup[0].hidden &&
            !singleGroup[0].active !== false
          ) {
            return <WizardHeading key={'heading-' + i} {...singleGroup[0]} />;
          }
          const singleGroupFields = singleGroup
            .map((singleField) => {
              const fieldIndex = fieldNames
                ? fieldNames.indexOf(singleField.name)
                : -1;
              if (
                fieldIndex >= 0 &&
                !singleField.hidden &&
                singleField.active !== false &&
                singleField.type &&
                !metaAttributes?.[fieldIndex]?.props?.hidden
              ) {
                return (
                  <Field
                    {...getAdditionalProps(singleField)}
                    {...(singleField.props || {})}
                    // temporary solution: the whole _containerProps should come from the BE
                    // however it would mean adding it to every single component in the BE
                    // so for now we'll just add the pb: 6 there
                    _containerProps={{
                      pb: 6,
                      ...singleField?.props?._containerProps,
                    }}
                    fieldName={`${formName}.${singleField.name}`}
                    key={`${formName}-${singleField.name}-${i}`}
                    propMap={singleField.propMap}
                  />
                );
              }
              const descriptor = (componentDescriptors ?? []).find(
                (cd) =>
                  cd.props.name === `${formName}.${singleField.name}` &&
                  cd.props.position === singleField.position,
              );
              if (
                singleField.active !== false &&
                !singleField.hidden &&
                descriptor
              ) {
                return (
                  <ComponentRenderer
                    key={`${formName}-${singleField.name}-${i}`}
                    serializedComponent={{
                      ...descriptor,
                      props: {
                        ...descriptor.props,
                        mb: 6,
                      },
                    }}
                  />
                );
              }

              return null;
            })
            .filter((field) => field);

          return singleGroupFields.some((field) => !!field) ? (
            <Div key={'row-' + i}>
              {i !== 0 &&
              singleGroupFields[0]?.props?.serializedComponent?.type ===
                'WizardHeading' ? (
                <Div variant="divider" my={0} mt={4} />
              ) : null}
              <InputRow cols={singleGroup.length}>{singleGroupFields}</InputRow>
            </Div>
          ) : null;
        })
        .filter((groupedFields) => groupedFields),
    [groupedLegacyDefinitions, fieldNames, componentDescriptors],
  );

  const loadingStepText = useMemo(() => {
    return isLoading?.value === 1 && isCbMember?.value === 'ja'
      ? 'Laden...'
      : 'Volgende stap';
  }, [isLoading, isCbMember]);

  const canContinue = useMemo(() => {
    const steps = context?.initialSteps || [];
    const canContinueField = steps[0].fields.find(
      (field) => field.name === 'canContinue',
    );
    if (!canContinueField) return true;
    return !!canContinueField.value;
  }, [wizard, context]);

  const continueButton = useMemo(() => {
    if (!canContinue) {
      return null;
    }
    const stepsCount =
      legacyDefinition && legacyDefinition.length > 0
        ? legacyDefinition[0]?.steps?.length - 1
        : undefined;
    return currentStepIndex === stepsCount ? (
      <SubmitCurrentFormButton variant="primarySmall">
        <P variant="bodyImportantLight">{'Aanvragen'}</P>
        <P variant="captionLight">{'en per maand betalen'}</P>
      </SubmitCurrentFormButton>
    ) : (
      <SubmitCurrentFormButton variant="primary">
        {loadingStepText}
      </SubmitCurrentFormButton>
    );
  }, [
    currentStepIndex,
    wizard,
    loadingStepText,
    legacyDefinition,
    canContinue,
  ]);

  if (!wizard || !fieldNames || !formName) return null;

  return (
    <Div {...props}>
      {constructedComponents}
      <Div mt={6}>{continueButton}</Div>
    </Div>
  );
};

export const ApplyPageLayoutCar: React.FC<ApplyPageLayoutProps> = ({
  legacyDefinition,
  componentDescriptors,
  ...props
}) => {
  const wizard = useWizardService();
  const [isLoading, isCbMember] = useFields([
    '*.isWaitingForCbApiResponse',
    '*.is_cb_member',
  ]);
  const wizardStepIndex = useCurrentStepIndex() ?? '';
  const { api, name, getStateFromLocalStorage, persistStateInLocalStorage } =
    useContext(ComparatorContext);
  const { state } = useForm(wizardStepIndex);
  const handlePeregrineEvent = usePeregrineEventHandler(props, state, api);
  const {
    loading: carComparisonLoading,
    tempProduct,
    premium,
    requestLogId,
  } = useContext(CarPremiumContext);

  const persistedState = getStateFromLocalStorage();
  const parsedCurrentSelectedProduct = JSON.parse(
    persistedState?.context?.initialSteps?.[3].fields?.[3]?.value as string,
  );
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const currentStepIndex = useCurrentStepIndex() ?? '';
  const formName = useFormAttribute('name', currentStepIndex);
  const fieldNames = useFieldsAttribute('name', currentStepIndex);
  const metaAttributes = useFieldsAttribute('meta', currentStepIndex);

  const context = wizard?.getSnapshot().context;

  const handleClick = async () => {
    const dataToStoreInCookie = {
      totalPriceYear: tempProduct?.price?.year?.total,
      totalPriceMonth: tempProduct?.price?.month?.total,
      comparisonId: requestLogId,
      hasPremiumChanged: premium.hasChanged,
    };
    setCarPremiumData(dataToStoreInCookie);
    await handlePeregrineEvent();

    if (tempProduct && parsedCurrentSelectedProduct?.provider?.id === '1139') {
      console.log(
        parsedCurrentSelectedProduct.id,
        tempProduct.id,
        persistedState?.context?.initialSteps?.map((step, index) =>
          index === 3
            ? {
                ...step,
                fields: step.fields?.map((field, fieldIndex) =>
                  fieldIndex === 3
                    ? {
                        ...field,
                        value: JSON.stringify({
                          ...parsedCurrentSelectedProduct,
                          id: tempProduct.id,
                        }),
                      }
                    : field,
                ),
              }
            : step,
        ) || [],
      );

      persistStateInLocalStorage({
        ...persistedState,
        context: {
          ...persistedState?.context,
          currentStepIndex: persistedState?.context?.currentStepIndex ?? 0,
          validators: persistedState?.context?.validators ?? {},
          initialSteps:
            persistedState?.context?.initialSteps?.map((step, index) =>
              index === 3
                ? {
                    ...step,
                    fields: step.fields?.map((field, fieldIndex) =>
                      fieldIndex === 3
                        ? {
                            ...field,
                            value: JSON.stringify({
                              ...parsedCurrentSelectedProduct,
                              id: tempProduct.id,
                            }),
                          }
                        : field,
                    ),
                  }
                : step,
            ) || [],
        },
      });
    }

    setTimeout(() => {
      if (wizard) {
        wizard.send(['SUBMIT']);
      }
    }, 100);
  };

  const currentStepDefinition = legacyDefinition?.[0]?.steps?.[
    currentStepIndex
  ] ?? { components: [] };
  const groupedLegacyDefinitions = useMemo(() => {
    const { components, __typename = '' } = currentStepDefinition;
    let currentIndex = -1;
    let previousRow = 0;
    const groupedRows: Array<Array<any>> = [];

    const adjustedComponents = getAdditionalDefinition(components, __typename);
    (adjustedComponents ?? []).forEach(
      (legacyComponentDefinition: LegacyComponentDefinition) => {
        if (
          !legacyComponentDefinition.row ||
          legacyComponentDefinition.row === 'prequestions' ||
          legacyComponentDefinition.row !== previousRow
        ) {
          currentIndex += 1;
          if (
            typeof legacyComponentDefinition.row === 'number' &&
            legacyComponentDefinition.row !== previousRow
          ) {
            previousRow = Number(legacyComponentDefinition.row);
          } else {
            previousRow = 0;
          }
        }
        if (!groupedRows[currentIndex]) {
          groupedRows.push([]);
        }
        groupedRows[currentIndex].push(legacyComponentDefinition);
      },
    );
    return groupedRows;
  }, [currentStepDefinition.components]);

  const constructedComponents = useMemo(
    () =>
      groupedLegacyDefinitions
        .map((singleGroup, i) => {
          if (
            singleGroup.length === 1 &&
            singleGroup[0].type === 'heading' &&
            !singleGroup[0].hidden &&
            !singleGroup[0].active !== false
          ) {
            return <WizardHeading key={'heading-' + i} {...singleGroup[0]} />;
          }
          const singleGroupFields = singleGroup
            .map((singleField) => {
              const fieldIndex = fieldNames
                ? fieldNames.indexOf(singleField.name)
                : -1;
              if (
                fieldIndex >= 0 &&
                !singleField.hidden &&
                singleField.active !== false &&
                singleField.type &&
                !metaAttributes?.[fieldIndex]?.props?.hidden
              ) {
                return (
                  <Field
                    {...getAdditionalProps(singleField)}
                    {...(singleField.props || {})}
                    // temporary solution: the whole _containerProps should come from the BE
                    // however it would mean adding it to every single component in the BE
                    // so for now we'll just add the pb: 6 there
                    _containerProps={{
                      pb: 6,
                      ...singleField?.props?._containerProps,
                    }}
                    fieldName={`${formName}.${singleField.name}`}
                    key={`${formName}-${singleField.name}-${i}`}
                    propMap={singleField.propMap}
                  />
                );
              }
              const descriptor = (componentDescriptors ?? []).find(
                (cd) =>
                  cd.props.name === `${formName}.${singleField.name}` &&
                  cd.props.position === singleField.position,
              );
              if (
                singleField.active !== false &&
                !singleField.hidden &&
                descriptor
              ) {
                return (
                  <ComponentRenderer
                    key={`${formName}-${singleField.name}-${i}`}
                    serializedComponent={{
                      ...descriptor,
                      props: {
                        ...descriptor.props,
                        mb: 6,
                      },
                    }}
                  />
                );
              }

              return null;
            })
            .filter((field) => field);

          return singleGroupFields.some((field) => !!field) ? (
            <Div key={'row-' + i}>
              {i !== 0 &&
              singleGroupFields[0]?.props?.serializedComponent?.type ===
                'WizardHeading' ? (
                <Div variant="divider" my={0} mt={4} />
              ) : null}
              <InputRow cols={singleGroup.length}>{singleGroupFields}</InputRow>
            </Div>
          ) : null;
        })
        .filter((groupedFields) => groupedFields),
    [groupedLegacyDefinitions, fieldNames, componentDescriptors],
  );

  const loadingStepText = useMemo(() => {
    return isLoading?.value === 1 && isCbMember?.value === 'ja'
      ? 'Laden...'
      : 'Volgende stap';
  }, [isLoading, isCbMember]);

  const canContinue = useMemo(() => {
    const steps = context?.initialSteps || [];
    const canContinueField = steps[0].fields.find(
      (field) => field.name === 'canContinue',
    );
    if (!canContinueField) return true;
    return !!canContinueField.value;
  }, [wizard, context]);

  const continueButton = useMemo(() => {
    if (!canContinue) {
      return null;
    }
    const stepsCount =
      legacyDefinition && legacyDefinition.length > 0
        ? legacyDefinition[0]?.steps?.length - 1
        : undefined;
    if (name === 'car') {
      return currentStepIndex === stepsCount ? (
        <SubmitCarApplyButton
          variant="primarySmall"
          isLastStep={currentStepIndex === stepsCount}
        >
          <P variant="bodyImportantLight">{'Aanvragen'}</P>
          <P variant="captionLight">{'en per maand betalen'}</P>
        </SubmitCarApplyButton>
      ) : (
        <SubmitCarApplyButton
          variant="primary"
          isLastStep={currentStepIndex === stepsCount}
        >
          {loadingStepText}
        </SubmitCarApplyButton>
      );
    }
    return currentStepIndex === stepsCount ? (
      <SubmitCurrentFormButton variant="primarySmall">
        <P variant="bodyImportantLight">{'Aanvragen'}</P>
        <P variant="captionLight">{'en per maand betalen'}</P>
      </SubmitCurrentFormButton>
    ) : (
      <SubmitCurrentFormButton variant="primary">
        {loadingStepText}
      </SubmitCurrentFormButton>
    );
  }, [
    currentStepIndex,
    wizard,
    loadingStepText,
    legacyDefinition,
    canContinue,
  ]);

  useEffect(() => {
    if (premium.hasChanged) {
      setShowPremiumModal(true);
    }
  }, [premium]);

  useEffect(() => {
    if (tempProduct && parsedCurrentSelectedProduct?.provider?.id === '1139') {
      localStorage.setItem('ominimoProduct', JSON.stringify(tempProduct));
    }
  }, [tempProduct]);

  if (!wizard || !fieldNames || !formName) return null;

  return (
    <>
      <Window
        type="modal"
        show={!carComparisonLoading && showPremiumModal}
        title="Nieuwe premie"
        onClose={() => setShowPremiumModal(false)}
        showIcon
        modalProps={{
          show: !carComparisonLoading && showPremiumModal,
          title: 'Nieuwe premie',
          modalContainerDivProps: { width: '500px !important' },
          children: (
            <NewPremium
              currentPrice={premium.currentPrice}
              newPrice={premium.newPrice}
              onClick={handleClick}
              onClose={() => setShowPremiumModal(false)}
            />
          ),
        }}
      >
        <NewPremium
          currentPrice={premium.currentPrice}
          newPrice={premium.newPrice}
          onClick={handleClick}
          onClose={() => setShowPremiumModal(false)}
        />
      </Window>
      <Div {...props}>
        {constructedComponents}
        <Div mt={6}>{continueButton}</Div>
      </Div>
    </>
  );
};
